"use client";
import {
  ZSL,
  canvasAtom,
  isEditDescriptionModalOpenAtom,
  isKeyboardShortcutsModalOpenAtom,
  isDeleteCanvasModalOpenAtom,
  BuiltInApp,
  Session,
  Account,
  Toggles,
  RawCanvas,
} from "@zapier/canvas-state";
import { CurrentAccountIdProvider } from "@zapier/identity";
import "@zapier/product-nav/dist/style.css";
import { useAtomValue, useAtom } from "jotai";
import "jotai-devtools/styles.css";
import { ReactNode } from "react";
import { ReactFlowProvider } from "@xyflow/react";

import { useBindUndoRedoKeyboardShortcuts } from "../../hooks/useBindUndoRedoKeyboardShortcuts";
import useUpdateSelectedApps from "../../hooks/useUpdateSelectedApps";
import { TitleUpdater } from "../TitleUpdater";
import KeyboardShortcutsModal from "../KeyboardShortcutsModal/KeyboardShortcutsModal";
import { Notifications } from "../Notifications";
import { EditDescriptionModal } from "../modals/EditDescriptionModal";
import { DeleteModal } from "../modals/DeleteModal";
import { CanvasHydrator } from "../CanvasHydrator";
import { useEmitPageViewEvent } from "./useEmitPageViewEvent";
import { BuilderNav } from "../BuilderNav";
import { CanvasComposerHydrator } from "../CanvasComposer";

type Props = {
  children: ReactNode;
  canvas?: RawCanvas;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  account: Account;
  session: Session;
  toggles: Toggles;
};

export default function CanvasLayoutWrapper({
  children,
  canvas: canvasProp,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  account,
  session,
  toggles,
}: Props) {
  const canvas = canvasProp ?? (template?.canvases[0] as RawCanvas);

  return (
    <CurrentAccountIdProvider>
      <ReactFlowProvider>
        <CanvasComposerHydrator>
          <CanvasHydrator
            canvas={canvas}
            account={account}
            session={session}
            template={template}
            tablesApp={tablesApp}
            interfacesApp={interfacesApp}
            chatbotsApp={chatbotsApp}
            toggles={toggles}
          >
            <Notifications />
            <CanvasLayout>{children}</CanvasLayout>
          </CanvasHydrator>
        </CanvasComposerHydrator>
      </ReactFlowProvider>
    </CurrentAccountIdProvider>
  );
}

function CanvasLayout({ children }: { children: ReactNode }) {
  useEmitPageViewEvent();

  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useAtom(
    isEditDescriptionModalOpenAtom,
  );

  const [isDeleteCanvasModalOpen, setIsDeleteCanvasModalOpen] = useAtom(
    isDeleteCanvasModalOpenAtom,
  );

  const [isKeyboardShortcutsModalOpen, setIsKeyboardShortcutsModalOpen] =
    useAtom(isKeyboardShortcutsModalOpenAtom);

  const currentCanvas = useAtomValue(canvasAtom);

  useUpdateSelectedApps();
  useBindUndoRedoKeyboardShortcuts();

  return (
    <>
      <TitleUpdater title={currentCanvas.name || "Canvas"} />
      <BuilderNav>{children}</BuilderNav>
      {isEditDescriptionModalOpen && (
        <EditDescriptionModal
          onClosed={() => setIsEditDescriptionModalOpen(false)}
        />
      )}
      {isDeleteCanvasModalOpen && (
        <DeleteModal onClosed={() => setIsDeleteCanvasModalOpen(false)} />
      )}
      {isKeyboardShortcutsModalOpen && (
        <KeyboardShortcutsModal
          onClosed={() => setIsKeyboardShortcutsModalOpen(false)}
        />
      )}
    </>
  );
}
